/* eslint-disable @next/next/no-img-element */

import { OrganizationRegistry, Organizations } from '@/lib/config/organization'
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '@/lib/constants/legal'
import { ServiceModules } from '@/lib/constants/services'
import { useAPI } from '@aw/lib'
import { Button, Checkbox, Form, Heading, Input, Label, toast } from '@aw/ui'

import { ArrowRight } from '@aw/icons/outline'
import { CredentialResponse, GoogleLogin } from '@react-oauth/google'
import { UseMutationResult } from '@tanstack/react-query'
import Link from 'next/link'
import { ChangeEvent, useState } from 'react'
import { loginSchema } from '../utils/validation'
import { OlameeLogo } from '@/components/shared/olamee-logo'


interface LoginFormProps {
    submit: (data: {
        email: string
        password: string
        remember: boolean
    }) => void
    loginRequest: UseMutationResult<unknown, Error, unknown, unknown>
    onGoogleLoginSuccess: (data: { type: string }) => void
    formErrors: {
        message: string | undefined,
        errors: Record<string, string> | undefined
    }
}

export default function LoginForm({ loginRequest, submit, onGoogleLoginSuccess, formErrors }: LoginFormProps) {
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [remember, setRemember] = useState<boolean>(true)
    const [errors, setErrors] = useState<{ email: string[], password: string[] }>({ email: [], password: [] })
    const api = useAPI()

    const googleLoginRequest = api.post(`/login-with-google`, { service: ServiceModules.AUTH })



    const submitForm = async (event: React.FormEvent) => {
        event.preventDefault()

        const result = loginSchema.safeParse({ email, password })

        if (!result.success) {
            const newErrors: { email: string[], password: string[] } = { email: [], password: [] };
            result.error.issues.forEach(issue => {
                const field = issue.path[0] as 'email' | 'password';
                if (field === 'email' || field === 'password') {
                    newErrors[field].push(issue.message);
                }
            });
            setErrors(newErrors);
            return;
        }

        // Clear errors on successful validation
        setErrors({ email: [], password: [] });

        api.fetcher('/sanctum/csrf-cookie', {
            service: ServiceModules.AUTH,
            queryKey: ['/sanctum/csrf-cookie']
        })

        submit({ email, password, remember })
    }


    const socialIntegration = async (credentialResponse: CredentialResponse) => {
        await api.fetcher('/sanctum/csrf-cookie', {
            queryKey: ['/sanctum/csrf-cookie'],
            service: ServiceModules.AUTH,
        })
        googleLoginRequest.mutate(
            { token: credentialResponse.credential, remember: remember },
            {
                onSuccess: (res) => {
                    onGoogleLoginSuccess({ type: res.data?.type })
                },
                onError: (response) => { console.error(response); toast.error(response.data ? response?.data?.message : 'Something went wrong') }
            }
        )
    }

    const organization = Organizations[OrganizationRegistry.ABROADWORKS]


    function onChangeEmail(event: ChangeEvent<HTMLInputElement> | null) {
        setEmail(event?.target.value || '')
    }

    function onChangePassword(event: ChangeEvent<HTMLInputElement> | null) {
        setPassword(event?.target.value || '')
    }



    return (
        <div
            className="flex flex-col items-center gap-6 w-full "
        >
            {/* COMPANY LOGO */}
            <div className="flex w-full flex-col ">
                <div className="flex w-full select-none">

                    <div className="h-[3.5rem] w-auto">

                        <OlameeLogo className="h-full" />
                    </div>
                </div>
                <div className="flex flex-col gap-4 mt-6" >

                    <Heading size="h3" className="text-left !font-bold text-[2.25rem] text-gunmetal-800 dark:text-gunmetal-800-dark">
                        Get Started
                    </Heading>
                    <div className="text-gunmetal-500 dark:text-gunmetal-500-dark text-base">
                        Enter your email to continue
                    </div>
                </div>
            </div>

            {/* <div className="mb-9 flex flex-col items-center gap-9 w-max">
                                <Heading size="h2" className="text-center !font-inter text-[#0E1829]">Log in to your account </Heading>
                                <SocialAuth socialIntegration={socialIntegration} />
                                <div className="flex w-full items-center gap-2">
                                    <div className="h-[0.0625rem] flex-grow  bg-gunmetal-200"></div>
                                    <div className=" w-max px-2 text-center text-sm font-medium text-gunmetal-base">
                                        Or Continue with Email
                                    </div>
                                    <div className="h-[0.0625rem] flex-grow  bg-gunmetal-200"></div>
                                </div>
                            </div> */}
            <Form.FormErrors state={formErrors} className="mb-2 -mt-6 w-full" />

            <form id="login-form" onSubmit={submitForm} className=" w-full">
                <div className='flex flex-col gap-2'>
                    <Label htmlFor="email" required  >
                        Email Address
                    </Label>
                    <Input
                        id="login-email-input"
                        name="email"
                        type="email"
                        placeholder="john.doe@example.com"
                        value={email}
                        onErrorClear={() => setErrors(prev => ({ ...prev, email: [] }))}
                        disabled={loginRequest.isPending}
                        errorMessage={errors.email[0]}
                        hasError={errors.email.length > 0}
                        onChange={onChangeEmail}
                        autoFocus
                    ></Input>

                </div>
                <div className="my-4 w-full flex flex-col gap-2">
                    <Label htmlFor="password" required>
                        Password
                    </Label>
                    <Input
                        type="password"
                        id="login-password-input"
                        value={password}
                        placeholder="********"
                        onErrorClear={() => setErrors(prev => ({ ...prev, password: [] }))}
                        disabled={loginRequest?.isPending}
                        errorMessage={errors.password[0]}
                        onChange={onChangePassword}
                        hasError={errors.password.length > 0}
                    ></Input>

                </div>

                <div className="flex justify-between">
                    <div className="mb-6 flex items-center gap-2">
                        <Checkbox
                            id="remember-checkbox"
                            checked={remember}
                            onCheckedChange={() => setRemember(!remember)} />

                        <label htmlFor="remember-checkbox" className=" text-sm font-normal  !font-inter">
                            Stay signed in
                        </label>
                    </div>
                    <Link
                        id="forgot-password-link"
                        href="/forgot-password"
                        className=" text-sm font-normal text-purple-400 hover:text-purple-600">
                        Forgot Password?
                    </Link>
                </div>
                <div className="text-center text-sm leading-4 mb-4">
                    By using this website, you agree to our
                    <Link href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-400 hover:text-underline hover:text-purple-600  ml-1">Terms &amp; Conditions</Link> and
                    <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-400 hover:text-underline hover:text-purple-600  ml-1">Privacy Policy</Link>
                </div>

                <Button
                    id="login-submit-button"
                    type={"submit"}
                    variant="primary"
                    color='purple'
                    size={"md"}
                    className="group !font-inter !w-full"
                    loading={loginRequest?.isPending}
                    RightIcon={ArrowRight}
                >
                    Continue with email
                </Button>


            </form>
            <div className="flex w-full items-center gap-2">
                <div className="h-[0.0625rem] flex-grow  bg-[#969696]"></div>
                <div className="font-karla w-max px-2 text-center text-sm font-medium text-gunmetal-500 dark:text-gunmetal-500-dark">
                    Or Continue with
                </div>
                <div className="h-[0.0625rem] flex-grow  bg-[#969696]"></div>
            </div>
            <div className="flex w-full justify-center">
                <SocialAuth socialIntegration={socialIntegration} />
            </div>
        </div>

    )
}

function SocialAuth({ socialIntegration }: {
    socialIntegration: (credentialResponse: CredentialResponse) => Promise<void>

}) {


    return (
        <div id="social-auth-container" className="flex w-full flex-col items-center gap-4">
            <GoogleLogin
                width={314}
                onSuccess={async (credentialResponse) => {
                    await socialIntegration(credentialResponse);
                }
                }
                onError={() => {
                    console.error('Login Failed');
                }}
            />
        </div>
    )
}

